import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { HILL_COLORS } from './hillGeometry';

/**
 * The plain-language "what's shipping" list, rendered identically for the
 * logged-in viewer page and the public status page so the two never diverge.
 *
 * It shows framing, not mechanics: a phase, a target date, and whether the work
 * is on track. No Jira keys, no hill positions, no scopes — the payload it is
 * handed carries none of those to begin with.
 */

// The phases in the order work moves through them, each with the colour the hill
// already uses for that stretch of the slope (almost-done borrows the app's blue,
// since the hill has no dedicated colour for the tail of the descent).
const PHASE_ORDER = [
  { key: 'figuring_out', label: 'Figuring out', color: HILL_COLORS.figuring },
  { key: 'building', label: 'Building', color: HILL_COLORS.making },
  { key: 'almost_done', label: 'Almost done', color: '#3b82f6' },
  { key: 'shipped', label: 'Shipped', color: HILL_COLORS.done },
];

const tint = (hex, alpha) => {
  const value = hex.replace('#', '');
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * A target date as a person reads it, from a 'YYYY-MM-DD' string. Parsed at
 * local midnight so the day does not slip a timezone.
 */
const formatTargetDate = (targetDate) => {
  if (!targetDate) return null;
  const date = new Date(`${targetDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const OnTrackIndicator = ({ onTrack }) => {
  if (onTrack === null || onTrack === undefined) return null;
  if (onTrack) {
    return (
      <Chip
        size="small"
        icon={<CheckCircleOutlineIcon />}
        label="On track"
        sx={{
          backgroundColor: tint(HILL_COLORS.done, 0.15),
          color: HILL_COLORS.done,
          '& .MuiChip-icon': { color: HILL_COLORS.done },
        }}
      />
    );
  }
  return (
    <Chip
      size="small"
      icon={<WarningAmberIcon />}
      label="At risk"
      sx={{
        backgroundColor: tint(HILL_COLORS.making, 0.15),
        color: HILL_COLORS.making,
        '& .MuiChip-icon': { color: HILL_COLORS.making },
      }}
    />
  );
};

const StakeholderBetCard = ({ bet }) => {
  const targetDate = formatTargetDate(bet.targetDate);
  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {bet.name}
          </Typography>
          <OnTrackIndicator onTrack={bet.onTrack} />
        </Box>

        <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
          {bet.teamName && (
            <Typography variant="body2" color="text.secondary">
              {bet.teamName}
            </Typography>
          )}
          {targetDate && (
            <Typography variant="body2" color="text.secondary">
              {bet.phase === 'shipped' ? `Shipped in cycle ending ${targetDate}` : `Target: ${targetDate}`}
            </Typography>
          )}
        </Stack>

        {Array.isArray(bet.tags) && bet.tags.length > 0 && (
          <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 0.5 }}>
            {bet.tags.map((tag, index) => (
              <Chip key={`${tag.label}-${index}`} label={tag.label} size="small" variant="outlined" />
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

const StakeholderBetList = ({ view, bets }) => {
  const safeBets = Array.isArray(bets) ? bets : [];

  return (
    <Box>
      {view && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {view.name}
          </Typography>
          {view.description && (
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              {view.description}
            </Typography>
          )}
        </Box>
      )}

      {safeBets.length === 0 ? (
        <Typography color="text.secondary">Nothing in flight to show right now.</Typography>
      ) : (
        PHASE_ORDER.map((phase) => {
          const group = safeBets.filter(bet => bet.phase === phase.key);
          if (!group.length) return null;
          return (
            <Box key={phase.key} sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Chip
                  label={phase.label}
                  sx={{
                    backgroundColor: tint(phase.color, 0.15),
                    color: phase.color,
                    fontWeight: 600,
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {group.length} {group.length === 1 ? 'item' : 'items'}
                </Typography>
              </Box>
              <Stack spacing={2}>
                {group.map(bet => (
                  <StakeholderBetCard key={bet.id} bet={bet} />
                ))}
              </Stack>
            </Box>
          );
        })
      )}
    </Box>
  );
};

export default StakeholderBetList;
